import {
  Component,
  Plugins,
  Expressions,
  matchExpression,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {qualify, namesEqual} from '@packages/systems/wfdl-Qualified';
import {EElement, type Expression} from '@packages/systems/wfdl-ast';
import {getLibraryExportComponent} from './utils/getLibraryExportComponent';
import type {LayoutExport, LibraryExport} from './types';
import {concatTo, emptyArray, constant} from '@packages/utilities/fp/utils';

export type ReviewNote = {
  type: 'CUSTOM_CODE';
  customCodeMetadata: {
    exportIndex: number;
    customCode: string;
    elementId: string;
  };
};

type PartialNote = {
  type: 'CUSTOM_CODE';
  customCodeMetadata: {
    customCode: string;
    elementId: string;
  };
};

const HtmlEmbed = qualify(Plugins.name('Embed'), Component.name('HtmlEmbed'));
const customCodeExpressionPath = [
  {in: 'Record', at: 'embed'},
  {in: 'Record', at: 'meta'},
  {in: 'Record', at: 'html'},
] satisfies ExpressionPath;

const gatherNotesWithExpression = matchExpression({
  Element: (el) => {
    const isHtmlEmbed = namesEqual(el.type, HtmlEmbed);

    if (isHtmlEmbed) {
      const customCode = Expressions.getElementDataAt(
        el,
        customCodeExpressionPath,
        'Text',
        ''
      );
      return Expressions.stop([
        {
          type: 'CUSTOM_CODE',
          customCodeMetadata: {
            customCode,
            elementId: el.id,
          },
        },
      ]);
    }

    return Expressions.resume(emptyArray);
  },
  _: constant(Expressions.resume(emptyArray)),
});

const traverseExpression: (arg1: Expression) => PartialNote[] =
  Expressions.everything(concatTo)(gatherNotesWithExpression);

function getNoteForExport<T>(
  libraryExport: LayoutExport<T>,
  components: Component.ComponentMap
): PartialNote[] {
  const {rootElement} = getLibraryExportComponent({components, libraryExport});
  if (!rootElement) return [];
  return traverseExpression(EElement(rootElement));
}

export function getAllNotes<T>(
  libraryExports: Array<LibraryExport<T>>,
  components: Component.ComponentMap
): ReviewNote[] {
  return libraryExports.reduce<Array<ReviewNote>>(
    (res, libraryExport, index) => {
      if (libraryExport.type !== 'Layout') return res;
      const partialNotes = getNoteForExport(libraryExport, components);
      return res.concat(
        partialNotes.map((note) => ({
          ...note,
          customCodeMetadata: {
            exportIndex: index,
            ...note.customCodeMetadata,
          },
        }))
      );
    },
    []
  );
}

export type GetVersionedResourceNotesRes = Array<{
  noteType: ReviewNote['type'];
  layout: {
    displayName: string;
    category: string[];
    imageUrl: string;
  };
  issueMeta: {
    code: string;
  };
}>;
